'use client';
import React, { useRef, useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

export interface ParsedLineItem {
  description: string;
  hsn: string;
  qty: number;
  unit: string;
  rate: number;
  gstRate: number;
}

interface FileUploadParserProps {
  onParsed: (items: ParsedLineItem[]) => void;
}

const UNITS_VALID = ['Nos', 'Set', 'Job', 'Roll', 'Box', 'Mtr', 'Kg', 'Ltr', 'Sqft', 'Sqm', 'Pair', 'Lot'];
const GST_VALID = [0, 5, 12, 18, 28];

function normalizeUnit(val: string): string {
  if (!val) return 'Nos';
  const v = val.trim();
  const found = UNITS_VALID.find(u => u.toLowerCase() === v.toLowerCase());
  return found || 'Nos';
}

function normalizeGST(val: string | number): number {
  const n = Number(String(val).replace('%', '').trim());
  if (isNaN(n)) return 18;
  const found = GST_VALID.find(g => g === n);
  return found !== undefined ? found : 18;
}

// Map common column header aliases to canonical field names
const HEADER_MAP: Record<string, keyof ParsedLineItem> = {
  description: 'description',
  desc: 'description',
  item: 'description',
  'item description': 'description',
  'item name': 'description',
  name: 'description',
  particulars: 'description',
  hsn: 'hsn',
  'hsn/sac': 'hsn',
  'hsn code': 'hsn',
  sac: 'hsn',
  qty: 'qty',
  quantity: 'qty',
  'qty.': 'qty',
  unit: 'unit',
  uom: 'unit',
  'unit of measure': 'unit',
  rate: 'rate',
  'rate (₹)': 'rate',
  'unit rate': 'rate',
  price: 'rate',
  'unit price': 'rate',
  'rate (rs)': 'rate',
  gst: 'gstRate',
  'gst %': 'gstRate',
  'gst rate': 'gstRate',
  'gst%': 'gstRate',
  'tax %': 'gstRate',
  'tax rate': 'gstRate',
};

function parseRows(rows: string[][]): ParsedLineItem[] {
  if (rows.length < 2) return [];

  // Find header row (first row with recognizable columns)
  const headerRow = rows[0].map(h => (h || '').toString().trim().toLowerCase());
  const colMap: Record<keyof ParsedLineItem, number> = {
    description: -1,
    hsn: -1,
    qty: -1,
    unit: -1,
    rate: -1,
    gstRate: -1,
  };

  headerRow.forEach((h, i) => {
    const mapped = HEADER_MAP[h];
    if (mapped && colMap[mapped] === -1) {
      colMap[mapped] = i;
    }
  });

  // If description column not found, try positional fallback (col 0 = description)
  if (colMap.description === -1) colMap.description = 0;

  let items: ParsedLineItem[] = [];

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.every(c => !c)) continue; // skip empty rows

    const desc = colMap.description >= 0 ? String(row[colMap.description] || '').trim() : '';
    if (!desc) continue; // skip rows without description

    const hsn = colMap.hsn >= 0 ? String(row[colMap.hsn] || '').trim() : '';
    let qty = colMap.qty >= 0 ? Number(row[colMap.qty]) || 1 : 1;
    const unit = colMap.unit >= 0 ? normalizeUnit(String(row[colMap.unit] || '')) : 'Nos';
    let rate = colMap.rate >= 0 ? Number(String(row[colMap.rate] || '0').replace(/[₹,\s]/g, '')) || 0 : 0;
    let gstRate = colMap.gstRate >= 0 ? normalizeGST(row[colMap.gstRate]) : 18;

    items.push({ description: desc, hsn, qty, unit, rate, gstRate });
  }

  return items;
}

async function parseCSV(text: string): Promise<ParsedLineItem[]> {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const rows = lines.map(line => {
    // Handle quoted CSV fields
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  });
  return parseRows(rows);
}

async function parseExcel(buffer: ArrayBuffer): Promise<ParsedLineItem[]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];
  return parseRows(rows);
}

// Extract text from PDF using pdfjs-dist
async function parsePDF(buffer: ArrayBuffer): Promise<ParsedLineItem[]> {
  const pdfjsLib = await import('pdfjs-dist');
  // Use CDN worker to avoid bundling issues
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  let fullText = '';

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item: { str?: string }) => item.str || '')
      .join(' ');
    fullText += pageText + '\n';
  }

  return parseTextToItems(fullText);
}

// Extract text from DOCX using mammoth
async function parseDocx(buffer: ArrayBuffer): Promise<ParsedLineItem[]> {
  const mammoth = await import('mammoth');
  const result = await mammoth.extractRawText({ arrayBuffer: buffer });
  return parseTextToItems(result.value);
}

// Parse free-form text extracted from PDF/DOCX into line items
// Strategy: look for table-like rows with numbers (qty, rate) and text (description)
function parseTextToItems(text: string): ParsedLineItem[] {
  const lines = text
    .split(/\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0);

  let items: ParsedLineItem[] = [];

  // Pattern: line containing description + numbers that look like qty/rate
  // Try to detect rows that have at least a description and a numeric value (rate)
  // Heuristic: a "data row" has at least one number >= 1 and some text
  const numPattern = /[\d,]+(?:\.\d+)?/g;

  // First, try to detect if there's a structured table (tab/pipe/multi-space separated)
  const tableRows = lines.filter(l => /\t|\|/.test(l) || /\s{3,}/.test(l));

  if (tableRows.length >= 2) {
    // Try structured table parsing
    const structuredRows = tableRows.map(line => {
      if (line.includes('\t')) return line.split('\t').map(c => c.trim());
      if (line.includes('|')) return line.split('|').map(c => c.trim()).filter(c => c);
      // Multi-space split
      return line.split(/\s{2,}/).map(c => c.trim()).filter(c => c);
    });
    const parsed = parseRows(structuredRows);
    if (parsed.length > 0) return parsed;
  }

  // Fallback: heuristic line-by-line extraction
  for (const line of lines) {
    // Skip header-like lines
    if (/^(sl\.?\s*no|s\.?\s*no|#|sr|item|description|particulars|qty|rate|amount|total|hsn|gst)/i.test(line)) continue;
    // Skip lines that are purely numeric or very short
    if (line.length < 4) continue;

    const nums = (line.match(numPattern) || []).map(n => parseFloat(n.replace(/,/g, '')));
    const cleanNums = nums.filter(n => !isNaN(n) && n > 0);

    if (cleanNums.length === 0) continue;

    // Extract description: text portion before first significant number
    const firstNumIndex = line.search(/\d/);
    const descPart = firstNumIndex > 3 ? line.substring(0, firstNumIndex).trim() : '';
    if (!descPart || descPart.length < 3) continue;

    // Assign numbers heuristically: last large number = rate, second-to-last = qty if small
    let qty = 1;
    let rate = 0;
    let gstRate = 18;

    if (cleanNums.length === 1) {
      rate = cleanNums[0];
    } else if (cleanNums.length === 2) {
      qty = cleanNums[0] <= 9999 ? cleanNums[0] : 1;
      rate = cleanNums[1];
    } else {
      // Try to find GST (0,5,12,18,28), qty (small integer), rate (larger)
      const possibleGST = cleanNums.find(n => GST_VALID.includes(n));
      if (possibleGST !== undefined) gstRate = possibleGST;
      const remaining = cleanNums.filter(n => n !== possibleGST);
      if (remaining.length >= 2) {
        qty = remaining[0] <= 9999 ? remaining[0] : 1;
        rate = remaining[remaining.length - 1];
      } else if (remaining.length === 1) {
        rate = remaining[0];
      }
    }

    if (rate === 0 && cleanNums.length > 0) rate = cleanNums[cleanNums.length - 1];

    items.push({
      description: descPart.replace(/^\d+[\.\)]\s*/, '').trim(),
      hsn: '',
      qty,
      unit: 'Nos',
      rate,
      gstRate,
    });
  }

  return items;
}

export default function FileUploadParser({ onParsed }: FileUploadParserProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState<'idle' | 'parsing' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [fileName, setFileName] = useState('');
  const [previewItems, setPreviewItems] = useState<ParsedLineItem[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const processFile = useCallback(async (file: File) => {
    setFileName(file.name);
    setStatus('parsing');
    setMessage('');
    setPreviewItems([]);
    setShowPreview(false);

    try {
      let items: ParsedLineItem[] = [];
      const nameLower = file.name.toLowerCase();

      if (nameLower.endsWith('.csv')) {
        const text = await file.text();
        items = await parseCSV(text);
      } else if (nameLower.endsWith('.xlsx') || nameLower.endsWith('.xls')) {
        const buffer = await file.arrayBuffer();
        items = await parseExcel(buffer);
      } else if (nameLower.endsWith('.pdf')) {
        const buffer = await file.arrayBuffer();
        items = await parsePDF(buffer);
      } else if (nameLower.endsWith('.docx') || nameLower.endsWith('.doc')) {
        const buffer = await file.arrayBuffer();
        items = await parseDocx(buffer);
      } else {
        setStatus('error');
        setMessage('Unsupported file type. Please upload a .csv, .xlsx, .xls, .pdf, .docx, or .doc file.');
        return;
      }

      if (items.length === 0) {
        setStatus('error');
        setMessage('No valid line items found. Ensure the file contains a table with columns like Description, Qty, Rate, GST%.');
        return;
      }

      setPreviewItems(items);
      setShowPreview(true);
      setStatus('success');
      setMessage(`${items.length} item${items.length > 1 ? 's' : ''} parsed successfully`);
    } catch (err) {
      console.error('File parse error:', err);
      setStatus('error');
      setMessage('Failed to parse file. Ensure it is a valid Excel, CSV, PDF, or Word document.');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleApply = () => {
    onParsed(previewItems);
    setShowPreview(false);
    setStatus('idle');
    setFileName('');
    setPreviewItems([]);
  };

  const handleReset = () => {
    setStatus('idle');
    setFileName('');
    setMessage('');
    setPreviewItems([]);
    setShowPreview(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-700 text-slate-800 flex items-center gap-2 uppercase tracking-wider">
          <FileSpreadsheet size={15} className="text-[#E8620A]" />
          Import from File
        </h2>
        {status !== 'idle' && (
          <button
            type="button"
            onClick={handleReset}
            className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors"
          >
            <X size={13} />
            Clear
          </button>
        )}
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
        <Info size={13} className="text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700 leading-relaxed">
          Upload an <strong>Excel (.xlsx/.xls)</strong>, <strong>CSV</strong>, <strong>PDF</strong>, or <strong>Word (.docx/.doc)</strong> file with columns:{' '}
          <span className="font-mono bg-blue-100 px-1 rounded">Description</span>,{' '}
          <span className="font-mono bg-blue-100 px-1 rounded">HSN</span>,{' '}
          <span className="font-mono bg-blue-100 px-1 rounded">Qty</span>,{' '}
          <span className="font-mono bg-blue-100 px-1 rounded">Unit</span>,{' '}
          <span className="font-mono bg-blue-100 px-1 rounded">Rate</span>,{' '}
          <span className="font-mono bg-blue-100 px-1 rounded">GST%</span>.
          Parsed items will replace existing line items.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl px-6 py-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all duration-150 ${
          dragging
            ? 'border-[#E8620A] bg-[#E8620A]/5'
            : status === 'parsing' ? 'border-slate-200 bg-slate-50 cursor-wait' : 'border-slate-200 hover:border-[#E8620A]/60 hover:bg-slate-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.pdf,.docx,.doc"
          className="hidden"
          onChange={handleFileChange}
        />

        {status === 'parsing' ? (
          <>
            <div className="w-8 h-8 border-2 border-[#E8620A] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Parsing <span className="font-medium text-slate-700">{fileName}</span>…</p>
          </>
        ) : status === 'success' ? (
          <>
            <CheckCircle2 size={32} className="text-emerald-500" />
            <div className="text-center">
              <p className="text-sm font-semibold text-emerald-700">{message}</p>
              <p className="text-xs text-slate-500 mt-0.5">{fileName}</p>
            </div>
            <p className="text-xs text-slate-400">Drop another file to re-parse</p>
          </>
        ) : status === 'error' ? (
          <>
            <AlertTriangle size={32} className="text-amber-500" />
            <div className="text-center">
              <p className="text-sm font-semibold text-amber-700">Parse failed</p>
              <p className="text-xs text-slate-500 mt-1 max-w-xs">{message}</p>
            </div>
            <p className="text-xs text-slate-400">Click or drop to try again</p>
          </>
        ) : (
          <>
            <Upload size={28} className="text-slate-400" />
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-700">Drop your file here</p>
              <p className="text-xs text-slate-400 mt-0.5">or click to browse — .xlsx, .xls, .csv, .pdf, .docx, .doc</p>
            </div>
          </>
        )}
      </div>

      {/* Preview table */}
      {showPreview && previewItems.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
              Preview — {previewItems.length} item{previewItems.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 text-left font-600 text-slate-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left font-600 text-slate-500 uppercase tracking-wider min-w-[160px]">Description</th>
                  <th className="px-3 py-2 text-left font-600 text-slate-500 uppercase tracking-wider">HSN</th>
                  <th className="px-3 py-2 text-left font-600 text-slate-500 uppercase tracking-wider">Qty</th>
                  <th className="px-3 py-2 text-left font-600 text-slate-500 uppercase tracking-wider">Unit</th>
                  <th className="px-3 py-2 text-right font-600 text-slate-500 uppercase tracking-wider">Rate</th>
                  <th className="px-3 py-2 text-right font-600 text-slate-500 uppercase tracking-wider">GST%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {previewItems.map((item, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 text-slate-800 font-medium">{item.description}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono">{item.hsn || '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{item.qty}</td>
                    <td className="px-3 py-2 text-slate-700">{item.unit}</td>
                    <td className="px-3 py-2 text-right text-slate-700 tabular-nums">₹{item.rate.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{item.gstRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#E8620A] hover:bg-[#d05508] text-white rounded-lg text-xs font-semibold transition-all active:scale-95"
            >
              <CheckCircle2 size={13} />
              Apply to Form
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
